import { Button, debounce, IconButton, Stack } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierType,
  CarrierVisitDirection,
  ContainerInfoForTallyResponseDto,
  OrderResponseDto,
  SealsDto,
} from '@planning/app/api'
import { useUnitTypeLabel } from '@planning/components/UnitType'
import { createOogFormDataFromOrderResponse } from '@planning/components/organisms/OogSubForm/oog-helper'
import { createStowagePositionFormDataFromOrderResponse } from '@planning/components/stowage-position/stores/StowagePositionSubFormStore'
import { NavigationStackBackButton } from '@planning/pages/TallymanV2/Components/HeaderComponents/NavigationStackBackButton'
import { SearchContainerViewStore } from '@planning/rt-stores/tallyman'
import { containerService } from '@planning/services'
import { ErrorCode } from '@planning/utils/ErrorCode/error-code-enum'
import { isDomainModelException } from '@planning/utils/ErrorCode/error-code-utils'
import { useTranslate } from '@tolgee/react'
import { BinIcon, ContainerMobile, Header, Loader, StepperInfo, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  IInspectContainerFormData,
  InspectContainerForm,
} from './Components/InspectContainer/InspectContainerForm'
import { InspectContainerAlertType } from './Components/atoms/InspectContainerAlert'

interface IProps {
  store: SearchContainerViewStore
  submitButtonText: string
  lastSubmittedSeals: SealsDto[]
  onSubmit: (data: IInspectContainerFormData) => Promise<void> | void
  onDelete?: () => void
}

export const InspectContainer: FC<IProps> = observer(
  ({ store, submitButtonText, lastSubmittedSeals, onDelete, onSubmit }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const { containerItemStore, appViewStore, tallymanSelectVesselAndOperationViewStore } =
      usePlanningStore()
    const { vesselTallyStore } = tallymanSelectVesselAndOperationViewStore

    const [isLoading, setIsLoading] = useState(false)

    const orderItem = store.selectedOrder
    const damages = orderItem?.container?.data.damages

    let order: OrderResponseDto | undefined

    //When order is not found (e.g., overlanded container), create a new order object with minimal info
    if (orderItem) {
      order = orderItem.data
    } else {
      order = {
        containerNumber: store.searchFilter,
        direction: CarrierVisitDirection.Inbound,
        carrierVisitType: CarrierType.Vessel,
        carrierVisitId: vesselTallyStore.selectedVisitId,
      } as OrderResponseDto
    }

    const portOfDischarge = vesselTallyStore.getLocationNameByCode(order.portOfDischarge)
    const portOfLoading = vesselTallyStore.getLocationNameByCode(order.portOfLoading)

    const isOrderCreation = !order.id

    useEffect(() => {
      if (order.containerId) containerItemStore.fetchById(order.containerId)
    }, [containerItemStore, order.containerId])

    const [orderCreationContainerAlert, setOrderCreationContainerAlert] =
      useState<InspectContainerAlertType>(undefined)

    const containerItem = containerItemStore.elements[order.containerId ?? 0]

    const form = useForm<IInspectContainerFormData>({
      defaultValues: {
        id: order.id,
        referenceNumber: order.referenceNumber,
        direction: order.direction,
        carrierType: order.carrierVisitType,
        containerId: order.containerId,
        containerNumber: order.containerNumber,
        carrierVisitId: order.carrierVisitId,
        unitType: order.unitType,
        containerIsoCode: order.containerIsoCode ?? '',
        containerTare: order.containerTare ?? null,
        containerMaxGross: order.containerMaxGross ?? null,
        isEmpty: order.isEmpty,
        imoClasses: order?.imoClasses ?? [],
        holds: order.holds,
        damages: damages ?? containerItem?.data.damages ?? [],
        hasSeals: order.hasSeals || lastSubmittedSeals.length > 0,
        seals: order.seals?.length ? order.seals : lastSubmittedSeals,
        hasDamage: !!(damages?.length ?? containerItem?.data.damages.length),
        waggon: order.waggon,
        railTrackId: order.railTrackId,
        oog: (order as IInspectContainerFormData)?.oog ?? createOogFormDataFromOrderResponse(order),
        stowagePosition: createStowagePositionFormDataFromOrderResponse(order),
        portOfDischarge: portOfDischarge,
        portOfLoading: portOfLoading,
        vgm: order.vgm,
        grossWeight: order.grossWeight,
        operationalInstructions: order.operationalInstructions,
        notes: order.notes,
        shippingLine: order.customerName,
        journeyCategory: order.journeyCategory,
      },
    })

    const onFormSubmit = async (data: any) => {
      setOrderCreationContainerAlert(undefined)
      try {
        setIsLoading(true)
        await onSubmit(data)
        setIsLoading(false)
      } catch (error: any) {
        if (isDomainModelException(error, ErrorCode.ContainerAlreadyOnTerminal)) {
          setOrderCreationContainerAlert('onTerminal')
        } else {
          appViewStore.setShowAlert('error', t('failedToSave', 'Failed to save'))
        }
      } finally {
        setIsLoading(false)
      }
    }

    const getStepText = () => {
      const hasDamage = form.watch('hasDamage')
      return `1 ${t('of', 'of')} ${hasDamage ? 3 : 2}`
    }

    const containerStatus = order?.isEmpty ? t('empty', 'Empty') : t('full', 'Full')

    const title = `Inspect ${useUnitTypeLabel(containerItem?.data?.unitType)}`

    const setContainerInfoToForm = (containerInfo?: ContainerInfoForTallyResponseDto) => {
      form.setValue('containerIsoCode', containerInfo?.isoCode ?? '')
      form.setValue('containerMaxGross', containerInfo?.maxGross ?? null)
      form.setValue('containerTare', containerInfo?.tare ?? null)
      form.setValue('isEmpty', containerInfo?.isEmpty ?? false)
    }

    const debounceContainerNumberChange = useMemo(
      () =>
        debounce(async (containerNumber?: string) => {
          setOrderCreationContainerAlert(undefined)

          if (!containerNumber || containerNumber.length <= 0) {
            setContainerInfoToForm()
            return
          }

          try {
            const containerInfo =
              await containerService.getContainerInfoForTallyQuery(containerNumber)

            setContainerInfoToForm(containerInfo)

            if (!containerInfo) {
              return
            }

            if (containerInfo.isOnTerminal) {
              setOrderCreationContainerAlert('onTerminal')
            } else if (
              containerInfo.carrierVisits?.some(cv => cv.id === store.parentStore.selectedVisitId)
            ) {
              setOrderCreationContainerAlert('announced')
            } else {
              setOrderCreationContainerAlert('onSystem')
            }
          } catch {}
        }, 300),
      [],
    )

    return (
      <>
        <Header
          leftRenderOption={<NavigationStackBackButton />}
          title={isOrderCreation ? t('addNew', 'Add new') : order?.containerNumber ?? ''}
          rightRenderOption={
            <Stack flexDirection='row' gap={theme.customSpacing.xs} alignItems='center'>
              {onDelete && (
                <IconButton data-cy='delete-order-inspect-container' onClick={onDelete}>
                  <BinIcon />
                </IconButton>
              )}
              <Button
                data-cy='submit-inspect-container'
                variant='contained'
                sx={{ flex: 'none' }}
                onClick={form.handleSubmit(onFormSubmit)}
                disabled={
                  orderCreationContainerAlert === 'onTerminal' ||
                  orderCreationContainerAlert === 'announced'
                }
              >
                {submitButtonText}
              </Button>
            </Stack>
          }
        />
        <StepperInfo title={title} steps={getStepText()} />
        {order && (
          <ContainerMobile sx={{ pt: theme.customSpacing.l }}>
            {<Loader show={isLoading} />}
            <FormProvider {...form}>
              <InspectContainerForm
                containerItem={containerItem}
                isOverlanded={isOrderCreation}
                inspectContainerAlertType={orderCreationContainerAlert}
                containerStatus={containerStatus}
                onContainerNumberChange={debounceContainerNumberChange}
              />
            </FormProvider>
          </ContainerMobile>
        )}
      </>
    )
  },
)
